class ProductCarousel extends HTMLElement {
    constructor() {
        super();
        this.currentSlide = 0;
        this.slides = null;
        this.thumbnails = null;
        this.mainTrack = null;
        this.thumbTrack = null;
        this.paginationDots = null;

        // Touch/swipe variables
        this.startX = 0;
        this.startY = 0;
        this.currentX = 0;
        this.currentY = 0;
        this.isSwiping = false;
        this.isHorizontalSwipe = false;

        // Cached layout values to avoid forced synchronous layouts
        this.slideWidth = 0;
        this.thumbWidth = 0;
        this.currentThumbTranslate = 0;
        this.rafId = null;
    }

    connectedCallback() {
        this.slides = this.querySelectorAll('.carousel__slide');
        this.thumbnails = this.querySelectorAll('.thumbnail-item');
        this.mainTrack = this.querySelector('[data-carousel="main"]');
        this.thumbTrack = this.querySelector('[data-carousel="thumbnails"]');
        this.paginationDots = this.querySelectorAll('.carousel__pagination-dot');

        if (!this.mainTrack || !this.slides.length) return;

        // Cache layout values once
        this.cacheLayoutValues();

        // Recalculate on resize
        this.resizeObserver = new ResizeObserver(() => {
            this.cacheLayoutValues();
        });
        this.resizeObserver.observe(this.mainTrack);
        if (this.thumbTrack) {
            this.resizeObserver.observe(this.thumbTrack);
        }

        // Initialize first slide
        this.goToSlide(0);

        // Add touch listeners for mobile swipe
        this.mainTrack.addEventListener('touchstart', this.handleTouchStart.bind(this), {
            passive: false,
        });
        this.mainTrack.addEventListener('touchmove', this.handleTouchMove.bind(this), {
            passive: false,
        });
        this.mainTrack.addEventListener('touchend', this.handleTouchEnd.bind(this));

        // Initialize arrow navigation
        this.initArrowNavigation();

        // Initialize thumbnail clicks
        this.initThumbnailClicks();

        // Initialize pagination dots
        this.initPaginationDots();
    }

    disconnectedCallback() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
        }
    }

    cacheLayoutValues() {
        // Batch all layout reads together
        this.slideWidth = this.mainTrack.offsetWidth;
        if (this.thumbTrack && this.thumbnails.length > 0) {
            this.thumbWidth = this.thumbnails[0].offsetWidth + 8; // 8px gap
        }
    }

    goToSlide(index) {
        this.currentSlide = index;
        this.dataset.currentIndex = index;

        // Use requestAnimationFrame to batch DOM updates
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
        }

        this.rafId = requestAnimationFrame(() => {
            // Use cached slideWidth instead of reading from DOM
            this.mainTrack.style.transform = `translateX(-${index * this.slideWidth}px)`;

            // Update active thumbnail
            this.thumbnails.forEach((thumb, i) => {
                thumb.classList.toggle('active', i === index);
            });

            // Auto-scroll thumbnail carousel to show current thumbnail
            if (this.thumbTrack && this.thumbnails.length > 5 && this.thumbWidth > 0) {
                // Use cached currentThumbTranslate instead of parsing CSS
                const visibleThumbnails = 5;
                const currentThumbIndex = index;
                const firstVisibleIndex = Math.abs(this.currentThumbTranslate / this.thumbWidth);
                const lastVisibleIndex = firstVisibleIndex + visibleThumbnails - 1;

                // If current thumbnail is not visible, scroll to show it
                if (
                    currentThumbIndex < firstVisibleIndex ||
                    currentThumbIndex > lastVisibleIndex
                ) {
                    let targetTranslate;
                    if (currentThumbIndex < firstVisibleIndex) {
                        // Scroll to show current thumbnail at the beginning
                        targetTranslate = -currentThumbIndex * this.thumbWidth;
                    } else {
                        // Scroll to show current thumbnail at the end
                        targetTranslate =
                            -(currentThumbIndex - visibleThumbnails + 1) * this.thumbWidth;
                    }

                    // Ensure we don't scroll past boundaries
                    const maxTranslate =
                        -(this.thumbnails.length - visibleThumbnails) * this.thumbWidth;
                    targetTranslate = Math.max(
                        maxTranslate,
                        Math.min(0, targetTranslate)
                    );

                    this.currentThumbTranslate = targetTranslate;
                    this.thumbTrack.style.transform = `translateX(${targetTranslate}px)`;
                }
            }

            this.updatePaginationDots(index);
        });
    }

    handleTouchStart(e) {
        this.startX = e.touches[0].clientX;
        this.startY = e.touches[0].clientY;
        this.isSwiping = true;
        this.isHorizontalSwipe = false;
        this.mainTrack.style.transition = 'none';
    }

    handleTouchMove(e) {
        if (!this.isSwiping) return;

        this.currentX = e.touches[0].clientX;
        this.currentY = e.touches[0].clientY;

        const diffX = Math.abs(this.currentX - this.startX);
        const diffY = Math.abs(this.currentY - this.startY);

        // Only activate horizontal swipe if horizontal movement is greater than vertical
        // and we've moved at least 10px horizontally
        if (diffX > diffY && diffX > 10) {
            if (!this.isHorizontalSwipe) {
                this.isHorizontalSwipe = true;
            }
            e.preventDefault();

            // Use requestAnimationFrame to batch DOM updates during touch
            if (this.rafId) {
                cancelAnimationFrame(this.rafId);
            }

            const diff = this.currentX - this.startX;
            // Use cached slideWidth instead of reading from DOM
            const translateX = -(this.currentSlide * this.slideWidth) + diff;

            this.rafId = requestAnimationFrame(() => {
                this.mainTrack.style.transform = `translateX(${translateX}px)`;
            });
        }
    }

    handleTouchEnd() {
        if (!this.isSwiping) return;
        this.isSwiping = false;

        // Use requestAnimationFrame to batch transition update
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
        }

        this.rafId = requestAnimationFrame(() => {
            this.mainTrack.style.transition = 'all .3s ease-in-out';
        });

        // Only process swipe if it was a horizontal swipe
        if (this.isHorizontalSwipe) {
            const diff = this.startX - this.currentX;
            // Use cached slideWidth instead of reading from DOM
            const threshold = this.slideWidth * 0.3; // 30% of slide width

            if (Math.abs(diff) > threshold) {
                if (diff > 0 && this.currentSlide < this.slides.length - 1) {
                    this.goToSlide(this.currentSlide + 1);
                } else if (diff < 0 && this.currentSlide > 0) {
                    this.goToSlide(this.currentSlide - 1);
                } else {
                    this.goToSlide(this.currentSlide);
                }
            } else {
                this.goToSlide(this.currentSlide);
            }
        }

        this.isHorizontalSwipe = false;
    }

    initArrowNavigation() {
        this.querySelectorAll('.carousel-arrow').forEach((arrow) => {
            arrow.addEventListener('click', () => {
                const direction = arrow.dataset.direction;
                const carousel = arrow.dataset.carousel;

                if (carousel === 'main') {
                    if (
                        direction === 'next' &&
                        this.currentSlide < this.slides.length - 1
                    ) {
                        this.goToSlide(this.currentSlide + 1);
                    } else if (direction === 'prev' && this.currentSlide > 0) {
                        this.goToSlide(this.currentSlide - 1);
                    }
                } else if (carousel === 'thumbnails' && this.thumbTrack && this.thumbWidth > 0) {
                    // Use cached values instead of parsing CSS and reading DOM
                    const maxTranslate = -(this.thumbnails.length - 5) * this.thumbWidth;

                    if (direction === 'next' && this.currentThumbTranslate > maxTranslate) {
                        this.currentThumbTranslate = this.currentThumbTranslate - this.thumbWidth;

                        if (this.rafId) {
                            cancelAnimationFrame(this.rafId);
                        }
                        this.rafId = requestAnimationFrame(() => {
                            this.thumbTrack.style.transform = `translateX(${this.currentThumbTranslate}px)`;
                        });
                    } else if (direction === 'prev' && this.currentThumbTranslate < 0) {
                        this.currentThumbTranslate = this.currentThumbTranslate + this.thumbWidth;

                        if (this.rafId) {
                            cancelAnimationFrame(this.rafId);
                        }
                        this.rafId = requestAnimationFrame(() => {
                            this.thumbTrack.style.transform = `translateX(${this.currentThumbTranslate}px)`;
                        });
                    }
                }
            });
        });
    }

    initThumbnailClicks() {
        this.thumbnails.forEach((thumb, index) => {
            thumb.addEventListener('click', () => this.goToSlide(index));
        });
    }

    initPaginationDots() {
        if (!this.paginationDots || !this.paginationDots.length) return;

        this.paginationDots.forEach((dot, index) => {
            dot.addEventListener('click', () => this.goToSlide(index));
        });
    }

    updatePaginationDots(activeIndex) {
        if (!this.paginationDots || !this.paginationDots.length) return;

        this.paginationDots.forEach((dot, index) => {
            const isActive = index === activeIndex;
            dot.classList.toggle('active', isActive);
            dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
    }
}

customElements.define('product-carousel', ProductCarousel);